import React, { createContext, useContext, useReducer, useMemo, PropsWithChildren } from 'react';

import { TSerializedIncident } from '../api/types';

type TAction = {
    id?: keyof typeof initialState;
    value?: any;
    reset?: boolean,
};

export type TGlobalStateContext = {
    globalState: {
        selectedBusinessId?: string, // Business id selected on Step 1 of Report page and used to make new queries
        incidentList?: TSerializedIncident[] | null;  // Incident list previously fetched at Step 1 of Report page
        showList?: boolean; // Variable used at Step 1 of Report page
        tab?: number; // Tab used at profile page
    };
    setGlobalState: (action: TAction) => void;
};

// 1. Create Context
const GlobalStateContext = createContext<TGlobalStateContext | null>(null);

// 2. Create custom hook
export const useGlobalStateContext = () => {
    const context = useContext(GlobalStateContext);

    if (!context) {
        throw new Error('useGlobalStateContext must be used within a GlobalStateProvider');
    }
    return context;
};


const reducer = (state: TGlobalStateContext['globalState'], action: TAction) => {
    const updatedState = { ...state };

    if (action.id) {
        updatedState[action.id] = action.value;
    }

    return action.reset ? initialState : updatedState;
};

const initialState = {
    selectedBusinessId: '',
    incidentList: null,
    showList: false,
    tab: 0, // default tab ('Aberto')
};

// 3. Create Provider
export const GlobalStateProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const value = useMemo(() => {
        return { globalState: state, setGlobalState: dispatch }
    }, [state])

    return <GlobalStateContext.Provider value={value}>{children}</GlobalStateContext.Provider>;
};
